import { useMutation } from '@tanstack/react-query';
import { updateDealRestriction } from '@dealstack/frontend/shared/dataExchange/services/project.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';

interface Props {
	dealId: string;
	onUpdated?: () => void;
}

export const useDealRestriction = ({ dealId, onUpdated }: Props) => {
	const tAdminDashboard = useTranslationAdminDashboard();

	const { hasPermission: canManage } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(dealId),
		permissionRole: PermissionRole.ADMIN,
	});

	const { mutate, isPending } = useMutation({
		mutationFn: (variables: Parameters<typeof updateDealRestriction>[0]) =>
			updateDealRestriction(variables),
	});

	const setRestriction = (isRestricted: boolean) => {
		mutate(
			{ dealId, isRestricted },
			{
				onSuccess: () => {
					onUpdated?.();
					showToastV2({
						title: tAdminDashboard('deal_settings.restriction.updated', {
							defaultValue: 'Access restriction updated',
						}),
					});
				},
				onError: () => {
					showToastV2({
						state: 'error',
						title: tAdminDashboard('deal_settings.restriction.update_failed', {
							defaultValue: 'Could not update access restriction',
						}),
					});
				},
			}
		);
	};

	return { setRestriction, isPending, canManage };
};
